import { useUpdateAgentConfigurationVersion } from '@/queries/agent-configuration-versions';
import { logger } from '@/utils';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import Button from 'antd/es/button';
import Empty from 'antd/es/empty';
import message from 'antd/es/message';
import Modal from 'antd/es/modal';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { AVAILABLE_AGENT_TOOLS, type ToolConfig } from '@mai/types';

import { ToolSelector } from '@/components/CreateAgentForm/ToolSelector/ToolSelector.component';

const ToolItem = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.paddingSM}px ${theme.padding}px;
  background: ${theme.colorFillQuaternary};
  border: ${`${theme.lineWidth}px solid ${theme.colorBorder}`};
  border-radius: ${theme.borderRadius}px;
  margin-bottom: ${theme.marginXS}px;
  transition: all ${theme.motionDurationMid}s;

  &:hover {
    background: ${theme.colorFillTertiary};
    border-color: ${theme.colorPrimary};
  }

  .tool-info {
    flex: 1;
  }

  .tool-name {
    font-weight: ${theme.fontWeightStrong};
    color: ${theme.colorText};
    margin-bottom: ${theme.marginXXS / 2}px;
  }

  .tool-type {
    font-size: ${theme.fontSizeSM}px;
    color: ${theme.colorTextSecondary};
    font-family: ${theme.fontFamilyCode};
  }
`}
`;

interface ToolsConfigSectionProps {
  config: {
    toolConfig: ToolConfig;
  };
  versionId?: string;
}

export const ToolsConfigSection: React.FC<ToolsConfigSectionProps> = ({
  config,
  versionId,
}) => {
  const theme = useTheme();
  const updateVersionMutation = useUpdateAgentConfigurationVersion();
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showToolModal, setShowToolModal] = useState(false);
  const [tempToolConfig, setTempToolConfig] = useState<ToolConfig>([]);

  const [currentTools, setCurrentTools] = useState<ToolConfig>(
    config.toolConfig || [],
  );

  // Reset when config changes
  useEffect(() => {
    setCurrentTools(config.toolConfig || []);
    setHasChanges(false);
  }, [config.toolConfig]);

  const handleRemoveTool = useCallback(
    (toolType: string) => {
      const newTools = currentTools.filter((tool) => tool.type !== toolType);
      setCurrentTools(newTools);
      setHasChanges(true);
    },
    [currentTools],
  );

  const handleAddTools = useCallback(() => {
    setTempToolConfig(currentTools);
    setShowToolModal(true);
  }, [currentTools]);

  const handleToolModalOk = useCallback(() => {
    setCurrentTools(tempToolConfig);
    setHasChanges(true);
    setShowToolModal(false);
  }, [tempToolConfig]);

  const handleToolModalCancel = useCallback(() => {
    setShowToolModal(false);
    setTempToolConfig([]);
  }, []);

  const handleSave = useCallback(async () => {
    if (!versionId || !hasChanges) return;

    setIsSaving(true);
    try {
      await updateVersionMutation.mutateAsync({
        versionId,
        data: { toolConfig: currentTools },
      });

      void message.success('Tools configuration saved successfully');
      setHasChanges(false);
    } catch (error) {
      logger.error('Failed to save tools configuration:', error);
      void message.error('Failed to save tools configuration');
    } finally {
      setIsSaving(false);
    }
  }, [versionId, hasChanges, currentTools, updateVersionMutation]);

  const renderTools = useCallback(() => {
    if (currentTools.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No tools configured"
          style={{ margin: `${theme.marginXL}px 0` }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTools}
          >
            Add Tools
          </Button>
        </Empty>
      );
    }

    return (
      <>
        {currentTools.map((tool) => {
          const toolInfo =
            AVAILABLE_AGENT_TOOLS[
              tool.type as keyof typeof AVAILABLE_AGENT_TOOLS
            ];
          return (
            <ToolItem key={tool.type}>
              <div className="tool-info">
                <div className="tool-name">{toolInfo?.name || tool.type}</div>
                <div className="tool-type">{tool.type}</div>
              </div>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveTool(tool.type)}
              >
                Remove
              </Button>
            </ToolItem>
          );
        })}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddTools}
          block
          style={{ marginTop: `${theme.margin}px` }}
        >
          Add More Tools
        </Button>
      </>
    );
  }, [
    currentTools,
    handleAddTools,
    handleRemoveTool,
    theme.margin,
    theme.marginXL,
  ]);

  return (
    <>
      {renderTools()}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          padding: `${theme.padding}px 0`,
          borderTop: `${theme.lineWidth}px solid #f0f0f0`,
          marginTop: `${theme.marginLG}px`,
        }}
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!versionId || !hasChanges}
          block
        >
          Save Changes
        </Button>
      </div>

      <Modal
        title="Select Tools"
        open={showToolModal}
        onOk={handleToolModalOk}
        onCancel={handleToolModalCancel}
        width={`${theme.controlHeightLG * 20}px`}
        bodyStyle={{ padding: `${theme.marginLG}px` }}
      >
        <ToolSelector value={tempToolConfig} onChange={setTempToolConfig} />
      </Modal>
    </>
  );
};
